/**
 * Utility functions for order data operations.
 * Uses singleton store directly for all operations.
 */

import {
  AuthStatusChangeComments,
  isValidAuthStatusChangeComments,
} from "../data-model/authStatusChangeComments";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../store/UserGeneralInfoStore";

/**
 * Clears all order-related data from store for a specific order.
 * Removes: JCode docs, BO comments, CPT codes, Global BO value,
 * Existing JCode docs, J-codes, Auth status change comments.
 */
export const clearOrderData = (orderId: string) => {
  UserStore.removeItem(`${UserGeneralInfoContextKeys.JCODE_DOCS}_${orderId}`);
  UserStore.removeItem(`${UserGeneralInfoContextKeys.BO_COMMENTS}_${orderId}`);
  UserStore.removeItem(`${UserGeneralInfoContextKeys.CPT_CODES}_${orderId}`);
  UserStore.removeItem(
    `${UserGeneralInfoContextKeys.GLOBAL_BO_VALUE}_${orderId}`,
  );
  UserStore.removeItem(
    `${UserGeneralInfoContextKeys.EXISTING_JCODE_DOCS}_${orderId}`,
  );
  UserStore.removeItem(`${UserGeneralInfoContextKeys.J_CODES}_${orderId}`);
  UserStore.removeItem(
    `${UserGeneralInfoContextKeys.AUTH_STATUS_CHANGE_COMMENTS}_${orderId}`,
  );
};

/**
 * Pushes a new auth status change comment to the existing array.
 * Creates a new array if none exists.
 */
export const pushAuthStatusChangeComment = (
  orderId: string,
  comment: AuthStatusChangeComments,
) => {
  const key = `${UserGeneralInfoContextKeys.AUTH_STATUS_CHANGE_COMMENTS}_${orderId}`;
  const comments = UserStore.getItem(key);

  if (
    comments &&
    comments !== null &&
    isValidAuthStatusChangeComments(comments ?? [])
  ) {
    comments.push(comment);
    UserStore.setItem(key, comments ?? []);
  } else {
    UserStore.setItem(key, [comment]);
  }
};

/**
 * Removes an auth status change comment by its J-code.
 */
export const removeAuthStatusChangeCommentByJCode = (
  orderId: string,
  jCode: string,
) => {
  const key = `${UserGeneralInfoContextKeys.AUTH_STATUS_CHANGE_COMMENTS}_${orderId}`;
  const comments = UserStore.getItem(key) as AuthStatusChangeComments[] | null;

  if (comments && comments !== null) {
    const index = comments.findIndex((comment) => comment.j_code === jCode);
    if (index !== -1) {
      comments.splice(index, 1);
      UserStore.setItem(key, comments);
    }
  }
};

/**
 * Gets auth status change comments for an order.
 */
export const getAuthStatusChangeComments = (
  orderId: string,
): AuthStatusChangeComments[] | null => {
  const comments = UserStore.getItem(
    `${UserGeneralInfoContextKeys.AUTH_STATUS_CHANGE_COMMENTS}_${orderId}`,
  );
  return comments ?? null;
};
